/** Owner Console strings — the trader's own console (D1). */
export const owner = {
  // --- shell ---
  'o.nav.overview': 'Overview',
  'o.nav.administration': 'Administration',
  'o.nav.dashboard': 'Dashboard',
  'o.nav.animals': 'Animals',
  'o.nav.expenses': 'Expenses',
  'o.nav.reports': 'Reports',
  'o.nav.people': 'People',
  'o.nav.priceLock': 'Price Lock',
  'o.nav.monthlyCosts': 'Monthly costs',
  'o.nav.history': 'Who changed what',
  'o.nav.backup': 'Backup & data',
  'o.nav.deleted': 'Deleted',
  'o.nav.settings': 'Settings',

  // --- O-01 Overview ---
  'o.overview.greeting': 'Good morning, {name}',
  'o.overview.addAnimal': 'Add animal',
  'o.overview.backupNeverTitle': 'Your records have never been backed up',
  'o.overview.backupNeverBody': 'If this Mac were lost today, you would lose the whole book.',
  'o.overview.backupWarnTitle': 'Your records have not been backed up for {days} days',
  'o.overview.backupWarnBody':
    'If this Mac were lost today, you would lose {days} days of your book.',
  'o.overview.backupNow': 'Back up now',
  'o.overview.inStock': 'In stock',
  'o.overview.inStockSub': '{count} sold this season',
  'o.overview.moneyTiedUp': 'Money tied up',
  'o.overview.moneyTiedUpSub': 'Purchase plus everything since',
  'o.overview.costingYou': 'Costing you',
  'o.overview.costingYouSub': 'Feed, rent and labour',
  'o.overview.profitBooked': 'Profit booked',
  // Both come off `GET /dashboard`: `soldThisSeason`, and
  // `profitBookedDaysHeld`, which the contract describes as this tile's sub-line.
  'o.overview.profitBookedSub': '{count} sold, held {days} days',
  'o.overview.recentActivity': 'Recent activity',
  'o.overview.colWhatChanged': 'What changed',
  // "Asif" was a person out of the design deck. The rule is about ROLES, and the
  // roles are what the panel beside this line is showing.
  'o.overview.peopleNote':
    'Only Farm Staff is blocked from prices. A Partner sees everything you see, apart from this panel.',
  'o.overview.signedInNow': 'Signed in now',
  'o.overview.emptyTitle': 'Nothing recorded yet',
  'o.overview.emptyBody': 'Add your first animal and this page fills itself in.',

  // --- O-02 People ---
  'o.people.title': 'People',
  'o.people.subtitle': '{count} accounts can open this book',
  'o.people.add': 'Add a person',
  'o.people.colSeesMoney': 'Sees money',
  'o.people.you': 'you',
  'o.people.matrixTitle': 'What each one can see',
  'o.people.matrixAnimals': 'Animals, photos, videos',
  'o.people.matrixExpenses': 'Record expenses and weights',
  'o.people.matrixPrices': 'Prices, cost and profit',
  'o.people.matrixSale': 'Record a sale',
  'o.people.matrixAdmin': 'People, settings, backups',
  'o.people.matrixOptional': 'Optional',
  'o.people.howStaffTitle': 'How staff are kept out of money',
  'o.people.howStaff1':
    'Prices are never sent to their device — there is nothing in the page to uncover',
  'o.people.howStaff2': 'No Reports tab at all, rather than a greyed-out one',
  'o.people.howStaff3': 'No sorting by value, which would leak the ranking',
  'o.people.howStaff4':
    'Break-even is hidden too — it is purchase price plus a known daily rate',
  'o.people.resetPassword': 'Reset password',
  'o.people.editRole': 'Edit role',
  'o.people.deactivate': 'Deactivate',
  'o.people.deactivateTitle': 'Deactivate {name}?',
  'o.people.deactivateBody':
    'They can no longer sign in. Everything they recorded stays in the book, and in the history, under their name.',
  'o.people.lastOwner': 'You cannot deactivate the last owner of a book.',

  // --- O-03 Add a person ---
  'o.newPerson.title': 'Add a person',
  'o.newPerson.subtitle': 'They will sign in with the username and password you set here.',
  'o.newPerson.theirName': 'Their name',
  'o.newPerson.whatTheyCanDo': 'What they can do',
  'o.newPerson.partnerTag': 'Sees money',
  'o.newPerson.staffTag': 'No money',
  'o.newPerson.partner1': 'All prices, cost and profit',
  'o.newPerson.partner2': 'Add animals, expenses and sales',
  'o.newPerson.partner3': 'Cannot add people or change settings',
  'o.newPerson.partner4': 'Cannot delete or restore backups',
  'o.newPerson.staff1': 'Add animals, photos and weights',
  'o.newPerson.staff2': 'Record a vet or medicine cost',
  'o.newPerson.staff3': 'Never sees a price or profit',
  'o.newPerson.staff4': 'No reports at all',
  'o.newPerson.firstPassword': 'Their first password',
  'o.newPerson.firstPasswordHint':
    'Read it out once. They must change it when they first sign in.',
  'o.newPerson.shownOnce': 'This password is shown once and cannot be shown again.',
  'o.newPerson.regenerate': 'Generate another',
  'o.newPerson.submit': 'Add {name} as {role}',
  'o.newPerson.submitEmpty': 'Add this person',
  'o.newPerson.nameRequired': 'A name is required.',
  'o.newPerson.usernameRequired': 'A username is required.',

  // --- O-04 Who changed what ---
  'o.history.title': 'Who changed what',
  'o.history.subtitle': 'Every edit to a price, an animal or an account',
  'o.history.searchPlaceholder': 'Search an animal, a person, an amount',
  'o.history.filterEverything': 'Everything',
  'o.history.filterMoney': 'Money',
  'o.history.filterPeople': 'People',
  'o.history.filterDeleted': 'Deleted',
  // CONTRACT GAP: `GET /audit-log` is fetched with no `from`/`to`, so there is
  // no date range on this screen — the button that claimed one is gone and this
  // says what is actually shown.
  'o.history.noRange': 'The newest changes first. There is no date filter on this screen yet.',
  'o.history.immutable': 'Nothing on this page can be edited or removed, including by you.',
  'o.history.restore': 'Restore',
  'o.history.emptyTitle': 'No changes recorded',
  'o.history.emptyBody': 'Once anyone edits a price or an animal, it appears here.',
  // The audit row's sentence. The API says which entity and which action; the
  // wording is a string, so it is composed here and not on the server.
  'o.history.entry': '{action} a {entity}',
  'o.history.entryWithLabel': '{action} {entity} · {label}',
  'o.history.action.CREATE': 'Added',
  'o.history.action.UPDATE': 'Changed',
  'o.history.action.DELETE': 'Deleted',
  'o.history.action.RESTORE': 'Restored',
  'o.history.action.LOGIN': 'Signed in to',
  'o.history.action.EXPORT': 'Exported',
  'o.history.action.SETTING': 'Changed a setting on',
  'o.history.platformVisit': 'Opened your book',
  'o.history.entryField': '{action} {entity} · {field}',
  /*
    WHAT CATTLE BOOK DID TO THIS BOOK — ARCH §8.6.

    Every act of the platform against a trader's book is mirrored into HIS log,
    so these are his sentences, not ours: he reads "Your book was made
    read-only", not `business_read_only`. Each one is said in the passive and
    names Cattle Book rather than the engineer, because the Who column already
    carries the person and the trader's question is what happened to his book.

    None of them quotes a figure, a date or a name: the payload carries one
    changed field per row and the Was / Now columns show it.
  */
  'o.history.platform.notified': 'Cattle Book sent you a message',
  'o.history.platform.invoiceReminded': 'Cattle Book sent you a reminder about your bill',
  'o.history.platform.invoiceRetried': 'Cattle Book tried to collect your bill again',
  'o.history.platform.readOnly': 'Your book was made read-only',
  'o.history.platform.suspended': 'Your book was suspended',
  'o.history.platform.closed': 'Your book was closed',
  'o.history.platform.trialExtended': 'Your trial was extended',
  'o.history.platform.created': 'Your book was opened on Cattle Book',
  // The honest last resort. The audit payload carries the field that moved but
  // NOT the action code behind it, so a row nothing above recognises names the
  // field rather than guessing at what the platform meant by it.
  'o.history.platform.otherField': 'Cattle Book changed {field} on your book',
  'o.history.platform.other': 'Cattle Book made a change to your book',
  // TENANT_STATUSES as the owner of the book would say them, used when the
  // field that moved is his book's own status. The platform console has its own
  // words for these; a code in the Now column is nobody's words.
  'o.history.bookStatus.PENDING_VERIFICATION': 'Waiting to be verified',
  'o.history.bookStatus.TRIAL': 'On trial',
  'o.history.bookStatus.ACTIVE': 'Active',
  'o.history.bookStatus.PAST_DUE': 'Payment overdue',
  'o.history.bookStatus.READ_ONLY': 'Read-only',
  'o.history.bookStatus.SUSPENDED': 'Suspended',
  'o.history.bookStatus.CANCELLED': 'Closed',
  // ACCESS_MODES, shown in the trader's OWN history when support has been in.
  'o.history.mode.READ_ONLY': 'Read-only',
  'o.history.mode.EDIT': 'Editing',
  // TRASH_ENTITIES plus the two the audit log also writes against.
  'o.history.entity.animal': 'an animal',
  'o.history.entity.expense': 'an expense',
  'o.history.entity.sale': 'a sale',
  'o.history.entity.payment': 'a payment',
  'o.history.entity.party': 'a buyer or seller',
  'o.history.entity.lot': 'a lot',
  'o.history.entity.tenant': 'this business',
  'o.history.entity.user': 'a person',
  'o.history.entity.feedPlan': 'the feed rate',
  'o.history.entity.recurringExpense': 'a monthly cost',
  'o.history.entity.media': 'a photo or video',

  // --- O-05 Backup & data ---
  'o.backup.title': 'Backup & data',
  'o.backup.subtitle': 'Your book is a single file. Keep a copy somewhere else.',
  'o.backup.lastFull': 'Last full backup',
  'o.backup.never': 'Never',
  'o.backup.neverBody': 'This book has never been backed up. Nothing is saved anywhere but this Mac.',
  'o.backup.daysAgo': '{days} days ago',
  'o.backup.savedTo': '{date} · saved to Downloads',
  'o.backup.backupEverything': 'Back up everything now',
  'o.backup.bookOnly': 'Book only ({size})',
  'o.backup.sizeNote':
    'The full backup is about {size} because it includes every photo and video. Put it on Google Drive or a USB stick.',
  'o.backup.whatIsStored': 'What is stored',
  'o.backup.photosVideo': 'Photos & video',
  'o.backup.olderCopies': 'Older copies',
  'o.backup.theBookItself': 'The book itself',
  'o.backup.bookNote':
    'The book is tiny. If you are ever in a hurry, take the book-only copy — the photographs can be replaced, the record cannot.',
  'o.backup.automatic': 'Automatic copies',
  'o.backup.running': 'Running',
  // The hour is `backupListResponse.nightlyHourPkt` — platform-configured, and
  // in the trader's own payload. `null` means the platform has not said.
  'o.backup.lastNight': 'Last night, {time}',
  'o.backup.lastNightNoHour': 'Last night',
  'o.backup.saved': 'Saved',
  'o.backup.copiesKept': 'Copies kept',
  'o.backup.copiesKeptValue': '{count} nightly copies',
  'o.backup.recordsCheck': 'Records check',
  'o.backup.healthy': 'Healthy',
  'o.backup.automaticNote':
    'These live on this Mac. They protect you from a mistake, not from losing the Mac. That is what the download is for.',
  'o.backup.other': 'Other',
  'o.backup.exportExcel': 'Export to Excel',
  'o.backup.exportExcelHint': 'For you or your accountant',
  'o.backup.restore': 'Restore from a backup',
  'o.backup.restoreHint': 'Replaces everything in the book',
  'o.backup.restoreAction': 'Restore…',
  'o.backup.restoreTitle': 'Restore {name} from a backup?',
  'o.backup.restoreBody':
    'Everything recorded since that backup is replaced — animals, prices, expenses and photographs. A copy of the book as it stands right now is taken first, so this can itself be undone.',
  'o.backup.restoreSnapshot': 'A snapshot of today’s book is saved before anything is replaced.',

  // --- O-06 Settings ---
  'o.settings.title': 'Settings',
  'o.settings.subtitle': 'Every one of these changes a number somewhere else',
  'o.settings.yourBusiness': 'Your business',
  'o.settings.nameHint': 'Shown at the top of the app and on reports',
  'o.settings.numberFormat': 'How amounts look',
  'o.settings.numberFormatHint': 'Lakh and crore, or plain thousands',
  'o.settings.priceLock': 'Price Lock',
  'o.settings.priceLockHint': 'Cost, break-even and selling price',
  'o.settings.hidePrices': 'Hide prices until a PIN',
  'o.settings.hideAgain': 'Hide again after',
  'o.settings.hideAgainHint': 'Of not using the app',

  'o.settings.yourPin': 'Your PIN',
  // `pinChangedAt` is in `GET /settings`; the "2 weeks ago" was the deck's.
  // The value itself is rendered by <RelativeTime>, not formatted into t().
  'o.settings.pinChanged': 'Last changed',
  'o.settings.pinNeverChanged': 'A PIN is set. When it was last changed is not recorded.',
  'o.settings.pinNotSet': 'No PIN has been set yet',
  'o.settings.changePin': 'Change PIN',
  'o.settings.commission': 'Commission you usually pay',
  'o.settings.buyCommission': 'When buying',
  'o.settings.buyCommissionHint': 'Filled in for you on every purchase',
  'o.settings.sellCommission': 'When selling',
  'o.settings.sellCommissionHint': 'Used to work out break-even',
  'o.settings.otherSaleCosts': 'Other costs when selling',
  'o.settings.otherSaleCostsHint': 'Transport, mandi entry. Added to every break-even',
  'o.settings.commissionNote':
    'Break-even solves for commission on the sale price, not on your cost — otherwise it under-quotes you every time.',
  'o.settings.sellCommissionWarning':
    'Changing this moves the break-even on every animal you are holding, not only the next one you sell.',
  'o.settings.feed': 'Feed',
  'o.settings.dailyEstimate': 'Daily estimate',
  'o.settings.dailyEstimateHint': 'Used only on days no real bill covers',
  'o.settings.realBills': 'Real feed bills',
  'o.settings.realBillsHint': 'Wanda, toori and kuttar you actually bought',
  'o.settings.realityCheck': 'You are currently spending',
  'o.settings.realityCheckBody': 'a day per animal — {gap} more than this estimate.',
  'o.settings.realityCheckUnder': 'a day per animal — {gap} less than this estimate.',
  'o.settings.realityCheckMatch': 'a day per animal, which matches this estimate.',

  // --- O-02 ---
  'o.people.colCanDo': 'Can do',
  'o.people.deactivated': '{name} can no longer sign in.',

  // --- O-06 ---
  // The reality check is split so the rate and the gap are real <Money>
  // elements composed around the words, not rupees formatted into a variable.
  'o.settings.numberFormatIndian': '1,23,45,678',
  'o.settings.numberFormatWestern': '12,345,678',
  'o.settings.realityCheckPerDay': 'a day per animal \u2014',
  'o.settings.realityCheckMore': 'more than this estimate.',
  'o.settings.realityCheckLess': 'less than this estimate.',

  // --- O-02 / O-03 · the people writes (O1, O2) ---
  'o.people.editTitle': 'Edit {name}',
  'o.people.editSubtitle':
    'The role is the money boundary: it decides whether the server ever sends this person a price.',
  'o.people.noteLabel': 'Note',
  'o.people.noteHint': 'What they do, so the list reads like the yard.',
  'o.people.saved': '{name} is updated.',
  'o.people.resetTitle': 'Reset the password for {name}?',
  'o.people.resetBody':
    'Their current password stops working at once, and they must choose a new one the first time they sign in.',
  'o.people.resetConfirm': 'Reset it',
  'o.people.newPasswordTitle': 'The new password for {name}',
  'o.people.newPasswordBody':
    'This is the only time it is shown. Copy it now and read it to them — nobody can read it back, and it can only be replaced again.',
  'o.people.passwordCopied': 'Password copied.',
  'o.people.mustChange': 'They must choose their own password the first time they sign in.',

  // --- O-03 ---
  'o.newPerson.addedTitle': '{name} can now sign in',
  'o.newPerson.addedBody':
    'Give them the username and the password below. The password is shown once and cannot be shown again.',
  'o.newPerson.added': '{name} is added.',
  'o.newPerson.theirPassword': 'Their password',
  'o.newPerson.done': 'Done',

  // --- O-04 · the export ---
  // There is no audit-log export endpoint in the contract (O4 is a read only),
  // so the file is written here, from the rows on the screen. The toast says
  // exactly that rather than implying the server prepared anything.
  'o.history.exported': '{count} rows written to a CSV file in your browser.',
  'o.history.exportEmpty': 'There is nothing to export.',
  'o.history.exportLocked':
    'Prices are hidden, so the Was and Now columns are left blank. Unlock to include them.',
  'o.history.exportFilename': 'who-changed-what',

  // --- O-05 · the backup writes (O6, O7, O8) ---
  'o.backup.started': 'A copy is being made. It appears here once it is finished.',
  'o.backup.download': 'Download the last copy',
  'o.backup.downloadHint': 'A signed link, good for fifteen minutes',
  'o.backup.downloadAction': 'Get a link',
  'o.backup.downloadNone': 'There is no finished copy to download yet.',
  'o.backup.downloadTitle': 'Your download link',
  'o.backup.downloadExpires': 'Link expires',
  'o.backup.downloadSize': 'Size',
  // The signed URL is real and the signature is real; the address it names is
  // not served by any route in the contract. Offering a button that does
  // nothing would read as a broken download rather than an unbuilt one.
  'o.backup.downloadNotServed':
    'The link is signed and genuine, but nothing serves the address it points at yet — no route in the contract answers it. Copy it if you need it for a support ticket; it will not download until that route exists.',
  'o.backup.restoreNone': 'There is no finished copy to restore from.',
  'o.backup.restoreStarted':
    'The restore has started. A snapshot of today’s book was saved before anything was replaced.',
} as const satisfies Record<string, string>
